import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { SEO } from "../components/SEO";

const ENDPOINT = "https://lumora-chatbot.onrender.com/chat";

const CHIPS = [
  { icon: "⚙️", text: "I need to scale a Suzuki coupling from 100g to 10kg" },
  { icon: "💧", text: "We need sublimation purification to electronics grade" },
  { icon: "🏭", text: "Can LAMKO supply X kg per quarter of a custom intermediate?" },
  { icon: "🧬", text: "We need a deuterated intermediate at kg scale, can LAMKO synthesize?" },
  { icon: "🤝", text: "My supplier dropped my OLED material, can LAMKO take over?" },
];

// Pull an optional quick-replies block out of a reply, strip UI-protocol
// tokens and stray fences, and keep the rest as clean prose. Markdown bold
// is left in place and rendered by renderRich.
function parseAssistant(raw) {
  let t = raw || "";
  let quick = [];
  const qr = t.match(/```quick-replies\s*([\s\S]*?)```/i);
  if (qr) {
    try {
      const arr = JSON.parse(qr[1].trim());
      if (Array.isArray(arr)) quick = arr.filter((x) => typeof x === "string");
    } catch (e) {}
    t = t.replace(qr[0], "");
  }
  t = t
    .replace(/<<[^>]*>>/g, "")
    .replace(/```[\s\S]*?```/g, "")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
  return { body: t, quick };
}

// Render newlines and **bold** without a markdown library.
function renderRich(text) {
  const lines = (text || "").split("\n");
  return lines.map((line, li) => {
    const parts = line.split(/\*\*(.+?)\*\*/g);
    return (
      <React.Fragment key={li}>
        {parts.map((p, pi) => (pi % 2 === 1 ? <strong key={pi}>{p}</strong> : p))}
        {li < lines.length - 1 ? <br /> : null}
      </React.Fragment>
    );
  });
}

export function Lumi() {
  const { i18n } = useTranslation();
  const lang = (i18n && i18n.language ? i18n.language : "en").toLowerCase().indexOf("ko") === 0 ? "ko" : "en";

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [slow, setSlow] = useState(false);
  const convId = useRef("lamko-" + Math.random().toString(36).slice(2));
  const inputRef = useRef(null);
  const threadRef = useRef(null);
  const mode = "buyer";

  // Hide the duplicate floating LUMI widget on /lumi only; restore on leave.
  useEffect(() => {
    const st = document.createElement("style");
    st.id = "lumi-hide-float";
    st.textContent = "#lumi-launcher,#lumi-panel,#lumi-teaser{display:none !important;}";
    document.head.appendChild(st);
    return () => { st.remove(); };
  }, []);

  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [messages]);

  async function send(text) {
    const t = (text || "").trim();
    if (!t || streaming) return;
    setMessages((m) => [...m, { role: "user", text: t }, { role: "assistant", text: "" }]);
    setInput("");
    setSlow(false);
    setStreaming(true);
    const slowTimer = setTimeout(() => setSlow(true), 2500);
    try {
      const fd = new FormData();
      fd.append("message", t);
      fd.append("conversation_id", convId.current);
      fd.append("brand", "lamko");
      fd.append("lang", lang);
      fd.append("mode", mode);
      const r = await fetch(ENDPOINT, { method: "POST", body: fd });
      const reader = r.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf("\n")) >= 0) {
          const line = buf.slice(0, idx).trim();
          buf = buf.slice(idx + 1);
          if (!line.startsWith("data:")) continue;
          try {
            const ev = JSON.parse(line.slice(5).trim());
            if (ev.delta && typeof ev.delta.text === "string") {
              setSlow(false);
              setMessages((m) => {
                const c = m.slice();
                c[c.length - 1] = { ...c[c.length - 1], text: c[c.length - 1].text + ev.delta.text };
                return c;
              });
            }
          } catch (e) {}
        }
      }
    } catch (e) {
      setMessages((m) => {
        const c = m.slice();
        c[c.length - 1] = { ...c[c.length - 1], text: c[c.length - 1].text || "LUMI is unavailable right now. Please try again in a moment." };
        return c;
      });
    } finally {
      clearTimeout(slowTimer);
      setSlow(false);
      setStreaming(false);
    }
  }

  const started = messages.length > 0;

  return (
    <div style={{ backgroundColor: "#070B16", minHeight: "100vh" }} className="text-white">
      <SEO
        title="LUMI - from molecule to multi-ton"
        description="LUMI is LAMKO's agentic AI workspace. Tell LUMI what you need to make. We bring the chemistry, the scale, and the compliance, across CRDMO, CDMO, CMO and purification."
        descriptionKo="LUMI는 LAMKO의 에이전트형 AI 워크스페이스입니다. 무엇을 만들어야 하는지 LUMI에게 알려주세요. 화학, 규모, 규정 준수를 함께 제공합니다."
        path="/lumi"
      />

      <section style={{ background: "radial-gradient(circle at 18% 18%, rgba(234,88,12,0.20), transparent 42%), radial-gradient(circle at 82% 12%, rgba(59,130,246,0.16), transparent 44%), #070B16" }}>
        <div className="max-w-5xl mx-auto px-5 md:px-6 pt-10 md:pt-14 pb-6 text-center">
          <div className="text-xs tracking-widest uppercase" style={{ color: "#FB923C" }}>LUMI &middot; agentic AI workspace</div>
          <h1 className="mt-3 text-3xl sm:text-4xl md:text-5xl font-bold leading-tight">
            LUMI, <span style={{ color: "#FB923C" }}>from molecule to multi-ton</span>
          </h1>
          <p className="mt-4 text-sm sm:text-base md:text-lg max-w-2xl mx-auto" style={{ color: "#AEBBD0" }}>
            Tell LUMI what you need to make. We bring the chemistry, the scale, and the compliance.
          </p>
          <button
            onClick={() => { if (inputRef.current) inputRef.current.focus(); }}
            className="mt-6 inline-flex items-center px-6 py-3 rounded-lg font-semibold text-white"
            style={{ background: "linear-gradient(90deg,#EA580C,#F59E0B)" }}
          >
            Start a project with LAMKO
          </button>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-3 sm:px-4 md:px-6 pb-16">
        <div className="rounded-2xl overflow-hidden" style={{ background: "#0D1424", border: "1px solid #1E293B" }}>
          <div ref={threadRef} style={{ minHeight: started ? "55vh" : "120px", maxHeight: "580px", overflowY: "auto" }} className="px-4 md:px-6 py-5">
            {!started && (
              <div className="text-sm leading-relaxed" style={{ color: "#AEBBD0" }}>
                <p>Welcome to the LUMI workspace. Tell LUMI what you need to make, scale, or verify, and LUMI scopes it across our CRDMO, CDMO, CMO and purification services.</p>
                <p className="mt-3" style={{ color: "#7E8DA6" }}>Pick a starting point, or type your own below.</p>
              </div>
            )}
            {messages.map((m, i) => {
              if (m.role === "user") {
                return (
                  <div key={i} className="mb-4">
                    <div className="text-xs mb-1" style={{ color: "#FB923C" }}>You</div>
                    <div className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: "#FFFFFF" }}>{m.text}</div>
                  </div>
                );
              }
              const parsed = parseAssistant(m.text);
              const isLast = i === messages.length - 1;
              const showWaiting = streaming && isLast && !parsed.body;
              return (
                <div key={i} className="mb-4">
                  <div className="text-xs mb-1" style={{ color: "#5DCAA5" }}>LUMI</div>
                  <div className="text-sm leading-relaxed" style={{ color: "#C7D3E6" }}>
                    {showWaiting
                      ? (slow
                          ? <span style={{ color: "#7E8DA6" }}>LUMI is waking up, this can take a few seconds.</span>
                          : "...")
                      : renderRich(parsed.body)}
                  </div>
                  {parsed.quick.length > 0 && !(streaming && isLast) && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {parsed.quick.map((q, qi) => (
                        <button
                          key={qi}
                          onClick={() => send(q)}
                          className="text-sm px-3 py-2 rounded-lg transition-colors"
                          style={{ background: "rgba(96,165,250,0.08)", border: "1px solid rgba(96,165,250,0.28)", color: "#D7E1EE" }}
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {!started && (
            <div className="px-4 md:px-6 pb-2 grid grid-cols-1 lg:grid-cols-5 gap-2">
              {CHIPS.map((c) => (
                <button
                  key={c.text}
                  onClick={() => send(c.text)}
                  className="text-left text-sm px-4 py-3 rounded-xl transition-colors"
                  style={{ background: "rgba(96,165,250,0.08)", border: "1px solid rgba(96,165,250,0.28)", color: "#D7E1EE" }}
                >
                  <span className="mr-2">{c.icon}</span>{c.text}
                </button>
              ))}
            </div>
          )}

          <div className="px-4 md:px-6 py-4 flex items-end gap-3" style={{ borderTop: "1px solid #1E293B" }}>
            <textarea
              ref={inputRef}
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(input); } }}
              placeholder="Tell LUMI what you need to make, scale, or verify..."
              className="flex-1 bg-transparent outline-none text-sm md:text-base resize-none py-2"
              style={{ color: "#E5ECF7", maxHeight: "120px" }}
            />
            <button
              onClick={() => send(input)}
              disabled={streaming}
              className="text-white text-sm font-semibold px-5 py-2.5 rounded-lg flex-shrink-0"
              style={{ background: streaming ? "#5B3416" : "linear-gradient(90deg,#EA580C,#F59E0B)", opacity: streaming ? 0.7 : 1 }}
            >
              {streaming ? "..." : "Send"}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
